import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  DeleteOutline,
  AddCircleOutline,
  SaveOutlined,
  ClearOutlined,
} from "@mui/icons-material";
import useLoader from "../../../hooks/useLoader";
import useModal from "../../../hooks/useModal.js";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import TextArea from "../../../components/ui/TextArea";
import { createCustomField, updateCustomField } from "../../../services/customField";

const MODULES = ["timesheet"];
// const FIELD_TYPES = ["text", "number", "date", "checkbox", "dropdown", "textarea"];
const FIELD_TYPES = ["text"];

const AddCustomField = ({ refresh, existingField = null }) => {
  console.log(existingField, "existingField")
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const [dropdownOptions, setDropdownOptions] = useState(
    existingField?.options || []
  );

  const { register, control, watch, reset, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      module: existingField?.module || "",
      field_label: existingField?.field_label || "",
      field_type: existingField?.field_type || "text",
      placeholder: existingField?.placeholder || "",
      is_required: existingField?.is_required || false,
      is_active: existingField?.is_active !== false,
      max_words: existingField?.max_words ?? 50,
    },
  });

  const selectedFieldType = watch("field_type");

  const handleAddOption = () => {
    setDropdownOptions([...dropdownOptions, { label: "", value: "" }]);
  };

  const handleRemoveOption = (index) => {
    const newOptions = dropdownOptions.filter((_, i) => i !== index);
    setDropdownOptions(newOptions);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...dropdownOptions];
    newOptions[index][field] = value;
    setDropdownOptions(newOptions);
  };

  const onSubmit = async (data) => {
    try {
      // Validation
      if (!data.module) {
        toast.error("Module is required");
        return;
      }
      if (!data.field_label) {
        toast.error("Field label is required");
        return;
      }

      if (selectedFieldType === "dropdown") {
        const validOptions = dropdownOptions?.filter((opt) => opt.label && opt.value);
        if (validOptions.length === 0) {
          toast.error("Please add at least one dropdown option");
          return;
        }
        data.options = validOptions;
      } else {
        data.options = null;
      }

      showLoader();

      // Prepare the payload
      const payload = {
        module: data.module,
        field_key: data.field_label
          ?.trim()
          .toLowerCase()
          .replace(/\s+/g, "_"),
        field_label: data.field_label,
        field_type: data.field_type,
        placeholder: data.placeholder || null,
        is_required: data.is_required || false,
        is_active: data.is_active !== false,
        ...(data.options && { options: data.options }),
      };
      // Call API
      let response;
      if (existingField?.id) {
        response = await updateCustomField(existingField.id, payload);
      } else {
        response = await createCustomField(payload);
      }

      if (response?.data?.status === 200 || response?.data?.status === 201) {
        hideModal?.();
        refresh();
        toast.success(
          existingField
            ? "Custom field updated successfully"
            : "Custom field added successfully"
        );

        reset();
        setDropdownOptions([{ label: "", value: "" }]);
        hideModal?.();
      }
    } catch (error) {
      console.error("Error saving custom field:", error);
      const errorMessage =
        error?.response?.data?.message || "Failed to save custom field";
      toast.error(errorMessage);
    } finally {
      hideLoader();
    }
  };

  const handleReset = () => {
    reset();
    setDropdownOptions([{ label: "", value: "" }]);
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Module Field */}
        <div className="mb-3">
          <label
            style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px", display: "block" }}
          >
            Module <span style={{ color: "red" }}>*</span>
          </label>
          <select
            className="form-select"
            {...register("module", { required: "Module is required" })}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ced4da",
              fontSize: "14px",
            }}
          >
            <option value="">Select Module</option>
            {MODULES.map((mod) => (
              <option key={mod} value={mod}>
                {mod.charAt(0).toUpperCase() + mod.slice(1)}
              </option>
            ))}
          </select>
          {errors.module && <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.module.message}</p>}
        </div>

        {/* Field Key */}
        {/* <div className="mb-3">
          <label style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px", display: "block" }}>
            Field Key (snake_case) <span style={{ color: "red" }}>*</span>
          </label>
          <Input
            type="text"
            {...register("field_key", {
              required: "Field key is required",
              pattern: {
                value: /^[a-z_][a-z0-9_]*$/,
                message: "Field key must be in snake_case format",
              },
            })}
            placeholder="e.g., blood_group"
            style={{ padding: "8px 12px", fontSize: "14px" }}
          />
          {errors.field_key && (
            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.field_key.message}</p>
          )}
        </div> */}

        {/* Field Label */}
        <div className="mb-3">
          <label style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px", display: "block" }}>
            Field Label <span style={{ color: "red" }}>*</span>
          </label>
          <Input
            type="text"
            {...register("field_label", {
              required: "Field label is required",
              validate: {
                validFormat: (value) =>
                  /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(value.trim()) ||
                  "Only letters and spaces are allowed",

                maxLengthAfterTrim: (value) =>
                  value.trim().length <= 15 ||
                  "Field label must be 15 characters or less",
              },
            })}
            placeholder="e.g., Blood Group"
            style={{ padding: "8px 12px", fontSize: "14px" }}
          />
          {errors.field_label && (
            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.field_label.message}</p>
          )}
        </div>

        {/* Field Type */}
        <div className="mb-3">
          <label style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px", display: "block" }}>
            Field Type <span style={{ color: "red" }}>*</span>
          </label>
          <select
            className="form-select"
            {...register("field_type", { required: "Field type is required" })}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ced4da",
              fontSize: "14px",
            }}
          >
            <option value="">Select Field Type</option>
            {FIELD_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
          {errors.field_type && (
            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.field_type.message}</p>
          )}
        </div>

        {/* Max Words */}
        {/* <div className="mb-3">
          <label style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px", display: "block" }}>
            Max Words Allowed <span style={{ color: "red" }}>*</span>
          </label>
          <Input
            type="number"
            {...register("max_words", {
              required: "Max words is required",
              validate: {
                isInteger: (value) =>
                  Number.isInteger(Number(value)) || "Must be a whole number",
                inRange: (value) =>
                  (Number(value) >= 1 && Number(value) <= 500) ||
                  "Must be between 1 and 500",
              },
              valueAsNumber: true,
            })}
            placeholder="e.g., 50"
            style={{ padding: "8px 12px", fontSize: "14px" }}
          />
          {errors.max_words && (
            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.max_words.message}</p>
          )}
        </div> */}

        {/* Placeholder */}
        <div className="mb-3">
          <label style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px", display: "block" }}>
            Placeholder (Optional)
          </label>
          <Input
            type="text"
            {...register("placeholder", {
              validate: {

                maxLengthAfterTrim: (value) =>
                  value.trim().length <= 50 ||
                  "Place holder must be 50 characters or less",
              },
            })}
            placeholder="e.g., Enter blood group"
            style={{ padding: "8px 12px", fontSize: "14px" }}
          />
          {errors.placeholder && (
            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.placeholder.message}</p>
          )}
        </div>

        {/* Dropdown Options (conditional) */}
        {selectedFieldType === "dropdown" && (
          <div className="mb-3">
            <label style={{ fontWeight: "600", fontSize: "14px", marginBottom: "12px", display: "block" }}>
              Dropdown Options
            </label>
            <div
              style={{
                backgroundColor: "#fff",
                padding: "12px",
                borderRadius: "4px",
                border: "1px solid #e0e0e0",
              }}
            >
              {JSON.parse(dropdownOptions)?.map((option, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: index < dropdownOptions?.length - 1 ? "12px" : "0",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      type="text"
                      placeholder="Label"
                      value={option.label}
                      onChange={(e) => handleOptionChange(index, "label", e.target.value)}
                      style={{ padding: "8px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="text"
                      placeholder="Value"
                      value={option.value}
                      onChange={(e) => handleOptionChange(index, "value", e.target.value)}
                      style={{ padding: "8px 12px", fontSize: "14px" }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(index)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "8px",
                      color: "#dc3545",
                      marginTop: "0",
                    }}
                    title="Remove option"
                  >
                    <DeleteOutline fontSize="small" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddOption}
                style={{
                  backgroundColor: "#f0f0f0",
                  border: "1px dashed #999",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "14px",
                  color: "#333",
                  marginTop: "8px",
                }}
              >
                <AddCircleOutline fontSize="small" />
                Add Option
              </button>
            </div>
          </div>
        )}

        {/* Required Checkbox */}
        <div className="mb-3">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="is_required"
              {...register("is_required")}
              style={{ cursor: "pointer", width: "18px", height: "18px" }}
            />
            <label htmlFor="is_required" style={{ fontWeight: "600", fontSize: "14px", cursor: "pointer", margin: 0 }}>
              Required Field
            </label>
          </div>
        </div>

        {/* Active Checkbox */}
        {/* <div className="mb-4">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="is_active"
              {...register("is_active")}
              style={{ cursor: "pointer", width: "18px", height: "18px" }}
            />
            <label htmlFor="is_active" style={{ fontWeight: "600", fontSize: "14px", cursor: "pointer", margin: 0 }}>
              Active
            </label>
          </div>
        </div> */}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            paddingTop: "15px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-danger"
          >
            Clear
          </button>
          <button
            type="submit"
            className="btn btn-success"
          >
            {existingField ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCustomField;
